import { Link, useParams } from "react-router";
import { useProfile } from "../api/hooks";
import type { Profile } from "../api/types";
import { MarketCard } from "../components/market";
import { Badge, Card, Empty, QueryState, Stat } from "../components/ui";
import { chips, relative } from "../lib/format";

export function ProfilePage() {
  const { username = "" } = useParams();
  const q = useProfile(username);
  return <QueryState q={q}>{() => <ProfileView p={q.data!} />}</QueryState>;
}

function ProfileView({ p }: { p: Profile }) {
  const champion = p.user.badges.filter((b) => b.startsWith("champion:"));
  const open = p.positions.filter((x) => x.status !== "FINAL" && x.sharesM.some((s) => s > 0));
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{p.user.displayName}</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
          @{p.user.username}
          {champion.map((b) => <Badge key={b} tone="warn">🏆 Season {b.split(":S")[1]} champion</Badge>)}
        </div>
      </div>

      <Card className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Profit (all time)" value={chips(p.profitM, { signed: true })} tone={p.profitM >= 0 ? "yes" : "no"} />
        <Stat label="Net worth" value={chips(p.netWorthM)} />
        <Stat label="All-time rank" value={p.allTime ? `#${p.allTime.rank}` : "—"} />
        <Stat label={p.seasonNumber > 0 ? `Season ${p.seasonNumber}` : "Season"} value={p.season ? `#${p.season.rank} · ${chips(p.season.profitM, { signed: true })}` : "—"} />
      </Card>

      <section>
        <h2 className="mb-2 font-semibold">Open positions</h2>
        {open.length === 0 ? <Empty>No open positions.</Empty> : (
          <Card>
            <ul className="divide-y divide-line text-sm">
              {open.map((x) => (
                <li key={x.marketId} className="flex items-center justify-between gap-3 py-2">
                  <Link to={`/m/${x.marketId}`} className="min-w-0 truncate hover:underline">{x.title}</Link>
                  <span className="shrink-0 text-right text-muted">
                    {x.sharesM.map((s, i) => (s > 0 ? `${chips(s)} ${x.outcomes?.[i] ?? ""}` : null)).filter(Boolean).join(", ")}
                    {" · "}worth {chips(x.valueM)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>

      <section>
        <h2 className="mb-2 font-semibold">Recent trades</h2>
        {p.trades.length === 0 ? <Empty>No trades yet.</Empty> : (
          <Card>
            <ul className="divide-y divide-line text-sm">
              {p.trades.map((t) => {
                const m = p.markets[t.marketId];
                return (
                  <li key={t.id} className="flex justify-between gap-3 py-2">
                    <span className="min-w-0">
                      {t.side === "BUY" ? "Bought" : "Sold"} {chips(t.sharesM)} {m?.outcomes[t.outcome] ?? "shares"}{" "}
                      <span className="text-muted">in</span>{" "}
                      <Link to={`/m/${t.marketId}`} className="hover:underline">{m?.title ?? "a market"}</Link>
                    </span>
                    <span className="shrink-0 text-muted">{chips(t.amountM)} chips · {relative(t.at)}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </section>

      {p.createdMarkets.length > 0 && (
        <section>
          <h2 className="mb-2 font-semibold">Markets created</h2>
          <div className="grid gap-3 sm:grid-cols-2">{p.createdMarkets.map((m) => <MarketCard key={m.id} market={m} />)}</div>
        </section>
      )}
    </div>
  );
}
